import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Dropdown,
  Row,
  Col,
  Menu,
  Empty,
  Typography,
  Card,
  Tag,
  Divider
} from 'antd';
import { 
  ReloadOutlined,
  DownloadOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from './MainLayout';
import { fetchReport, fetchUsageStats } from '../store/slices/clientUsage';

// Helper function for date formatting
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

function formatIndianNumber(num) {
  const x = num.toString();
  const lastThree = x.slice(-3);
  const otherNumbers = x.slice(0, -3);

  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + 
         (otherNumbers ? "," : "") + lastThree;
}

function getLast12Months() {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const result = [];
  const now = new Date();

  // Start from current month and go backwards 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);

    result.push({
      month: months[d.getMonth()],
      year: d.getFullYear()
    });
  }

  return result;
}

function downloadCSV(data, filename = "data.csv") {
  if (!data || !data.length) {
    console.error("No data provided");
    return;
  }

  // Extract headers
  const headers = Object.keys(data[0]);

  // Convert to CSV string
  const rows = data.map(row =>
    headers.map(field => JSON.stringify(row[field] ?? "")).join(",")
  );

  const csvContent = [['Date', 'Total Session', 'Unique Device'].join(","), ...rows].join("\n");

  // Create Blob
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


function getMonthNumber(monthName) {
  const months = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12
  };

  return months[monthName] || null; // return null if invalid
}

const months = getLast12Months();

const { Title, Text } = Typography;

const UsageSummary = ({ accountId, totalSessions, uniqueDevices, period, status }) => {
  return (
    <Card
      style={{
        borderRadius: 12,
        padding: 6,
        marginBottom: '24px'
      }}
      bodyStyle={{ padding: 20 }}
      
    >
      <Row>
        <Col span={12}>
          <Text type="secondary">{`Account Id: ${accountId}`}</Text>
        </Col>
      </Row>
      <Divider />
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Usage Summary
          </Title>
          <Text type="secondary">Total usage statistics</Text>
        </Col>
        <Col>
          <Text type="secondary">Total Sessions</Text>
          <Title level={3} style={{ marginTop: 4 }}>
            {totalSessions.toLocaleString()}
          </Title>
        </Col>

        <Col >
          <Text type="secondary">Total Unique Devices</Text>
          <Title level={3} style={{ marginTop: 4 }}>
            {uniqueDevices.toLocaleString()}
          </Title>
        </Col>

      </Row>
    </Card>
  );
};

const ClientUsage = ({ accountId }) => {
  const dispatch = useDispatch();
  const { report, reportLoading, reportError, reportStats, reportStatsLoading, reportStatsError } = useSelector(state => state.clientUsage);
  const [selectedMonth, setSelectedMonth] = useState(months[0]);
  
  useEffect(() => {
    dispatch(fetchReport({ id: accountId, month: getMonthNumber(selectedMonth.month), year: selectedMonth.year }));
  }, [accountId, selectedMonth]);

    useEffect(() => {
      dispatch(fetchUsageStats({ id: accountId, month: getMonthNumber(selectedMonth.month), year: selectedMonth.year }));
    }, [accountId, selectedMonth]);

  const handleMenuClick = (e) => {
    setSelectedMonth(months[(Number(e.key))]);
  };

  const onRefreshData = () => {
    dispatch(fetchReport({ id: accountId, month: getMonthNumber(selectedMonth.month), year: selectedMonth.year }));
    dispatch(fetchUsageStats({ id: accountId, month: getMonthNumber(selectedMonth.month), year: selectedMonth.year }));
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      {months.map((month, index) => (
        <Menu.Item key={index}>{`${month.month} ${month.year}`}</Menu.Item>
      ))}
    </Menu>
  );

  const columns = [
    {
        title: 'Date',
        dataIndex: '_id',
        key: '_id',
        render: (text, record) =>
        <div className=''>
          {text}
        </div>
      },
    {
      title: 'Total Session',
      dataIndex: 'total_session',
      key: 'total_session',
      render: (text) => <p>
          {formatIndianNumber(text)}
        </p>
    },
    {
      title: 'Unique Device',
      dataIndex: 'unique_device',
      key: 'unique_device',
      render: (text = '') => (
        <p>
          {formatIndianNumber(text)}
        </p>
      ),
    }
  ];

  return (
    <div>
        <Row justify="space-between" align="middle" style={{
          marginBottom: '24px',
          position: 'absolute',
          zIndex: '2',
          top: '13px',
          right: '29px',
          // 
          }} className='action-cta' >
          <Col span={6}>
            {/* <SearchBar wait={600} onSearch={(val) => dispatch(setClientSearch(val))} placeholder='Search by Company Name, accountId' /> */}
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />} 
                onClick={onRefreshData}
                loading={reportLoading}
              >
                Refresh
              </Button>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={() => downloadCSV(report || [], `${selectedMonth.month} ${selectedMonth.year}`)}
              >
                Download all to csv
              </Button>

              <Dropdown overlay={menu} trigger={["click"]}>
                <Button
                  type="primary"
                  style={{ backgroundColor: "#5856D6", border: "none" }} // AWS-like orange
                >
                  {`Consumption period: ${selectedMonth.month} ${selectedMonth.year}`} <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <UsageSummary accountId={accountId} totalSessions={(report || []).reduce((a ,b) => a + b['total_session'], 0)} uniqueDevices={reportStats?.unique_session || 0} period={`${selectedMonth.month} ${selectedMonth.year}`} status="Active" />
        
          <Table
            columns={columns}
            dataSource={report || []}
            loading={reportLoading}
            rowKey="_id"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No data found"
                />
              )
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total}`,
            }}
          />
      </div>
  );
};

export default ClientUsage;
